import { css } from 'styled-components'
import { createSelector } from 'reselect'
import flow from 'lodash/flow'
import reduce from 'lodash/reduce'
import add from 'lodash/add'
import isNumber from 'lodash/isNumber'
import {
  breakpointTabletSelector,
  breakpointMobileLandscapeSelector,
  navigationBarOuterHeightSelector,
  navigationBarOuterHeightTabletSelector,
  navigationBarOuterHeightLandscapeSelector
} from '../selectors'

/**
 * Creates a mixin that's commonly used to set a component's height/min-height
 * to the viewport height minus the heights of any persistant layout elements.
 * @param {string} heightProperty - height or min-height
 * @param {Object} heightSelectors - height selectors for different breakpoints
 * @param {[Function]} heightSelectors.default - height selectors
 * @param {[Function]} heightSelectors.tablet - height selectors for tablet breakpoint
 * @param {[Function]} heightSelectors.landscape - height selectors for landscape breakpoint
 * @return {StyledComponentMixin}
 */
export function createViewportHeightMixin(heightProperty, heightSelectors) {
  const combineHeights = (...heights) => reduce(heights, add, 0)
  const declareStyles = height => `
    ${heightProperty}: calc(100vh - ${height}px);
  `
  const declareStylesWithSafeAreas = height =>
    withSafeAreas(
      heightProperty,
      `calc(100vh - ${height}px)`,
      ['top', 'bottom'],
      '-'
    )

  return css`
    /* min-height should be 100vh minus header/footer height minus 1px (border on header) */
    ${createSelector(
      ...heightSelectors.default,
      flow([combineHeights, declareStylesWithSafeAreas])
    )}

    ${breakpointTabletSelector} {
      ${createSelector(
        ...heightSelectors.tablet,
        flow([combineHeights, declareStylesWithSafeAreas])
      )}
    }

    ${breakpointMobileLandscapeSelector} {
      ${createSelector(
        ...heightSelectors.landscape,
        flow([combineHeights, declareStyles])
      )}
    }
  `
}

export function withSafeAreas(property, value, safeAreas, separator = '+') {
  if (isNumber(value)) value = `${value}px`
  const wrapWith = wrapper => variable => `${wrapper}(${variable})`
  const safeAreaVariables = safeAreas.map(
    safeArea => `safe-area-inset-${safeArea}`
  )
  const safeAreaConstants = safeAreaVariables.map(wrapWith('constant'))
  const safeAreaEnvs = safeAreaVariables.map(wrapWith('env'))
  const separatorStr = ` ${separator} `

  return css`
    ${property}: ${value};
    /* iOS < 11.2 */
    ${property}: calc(
      ${[value.replace('calc', ''), ...safeAreaConstants].join(separatorStr)}
    );
    /* iOS > 11.2*/
    ${property}: calc(
      ${[value.replace('calc', ''), ...safeAreaEnvs].join(separatorStr)}
    );
  `
}

/**
 * Height selectors to use with `createViewportHeightMixin` that get both the
 * top navigation bar heights.
 */
const navigationBarOuterHeightSelectors = {
  default: [navigationBarOuterHeightSelector],
  tablet: [navigationBarOuterHeightTabletSelector],
  landscape: [navigationBarOuterHeightLandscapeSelector]
}

/**
 * Mixin intended for use in components that need a height equal to the height
 * of the viewport minus the height of the navigation bar.
 * @type {StyledComponentMixin}
 */
export const viewportHeightWithNavigationBar = createViewportHeightMixin(
  'height',
  navigationBarOuterHeightSelectors
)
/**
 * Mixin intended for use in components that need a min-height equal to the
 * height of the viewport minus the height of the navigation bar.
 * @type {StyledComponentMixin}
 */
export const viewportMinHeightWithNavigationBar = createViewportHeightMixin(
  'min-height',
  navigationBarOuterHeightSelectors
)
